import { StyleSheet, Animated, TextInput, View, Platform, Pressable, } from 'react-native';
import React, { useCallback, useRef, useState, forwardRef } from 'react';
import BMButton from '../Button/BMButton';
import BMText from '../Text/BMText';
import { BMPrimitives, useBMTheme } from '../../theme';
import BMButtonIcon from '../ButtonIcon/BMButtonIcon';
const INPUT_HEIGHT = 42;
const BMInputField = forwardRef((props, ref) => {
    const { value, label, caption, captionAlignment = 'left', LeadingIcon, disabled, buttonInsetType = 'BUTTON', buttonInsetProps, error, variant = 'FIELD', editable = true, onChangeText, onDropdownToggle, onBlur, onFocus, testID, ...restProps } = props;
    const [isFocused, setisFocused] = useState(false);
    const [containerSize, setContainerSize] = useState({ height: 0, width: 0 });
    const inputRef = useRef(null);
    const ButtonInsetComponent = buttonInsetType === 'BUTTON' ? BMButton : BMButtonIcon;
    const showTextMessage = (typeof error === 'string' && error.length > 0) || (typeof caption === 'string' && caption.length > 0);
    // styles/color
    const { BMComponents } = useBMTheme();
    const getBorderColor = useCallback(() => {
        if (error) {
            return BMComponents.BMStyleLineBordercolorError;
        }
        else {
            return BMComponents.BMUiInputBordercolor;
        }
    }, [isFocused, error]);
    const measureSize = (e) => {
        const { width, height } = e.nativeEvent.layout;
        setContainerSize({ height: height + 2, width: width + 2 });
    };
    // handlers
    const handleTextChange = (value) => {
        onChangeText === null || onChangeText === void 0 ? void 0 : onChangeText(value);
    };
    const handleFocus = (e) => {
        if (!disabled) {
            setisFocused(true);
            onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
        }
    };
    const handleBlur = (e) => {
        if (!disabled) {
            setisFocused(false);
            onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
        }
    };
    const setTextRef = useCallback((node) => {
        inputRef.current = node;
        if (typeof ref === 'function') {
            ref(node);
        }
        else if (ref && 'current' in ref) {
            ref.current = node;
        }
    }, []);
    return (<>
      <BMText style={[
            styles.label,
            {
                color: disabled ? BMComponents.BMStyleTextInkMuted : BMComponents.BMStyleTextInkPrimary,
            },
        ]} variant="uiNormal">
        {label}
      </BMText>

      <Pressable onPress={variant === 'DROPDOWN' ? onDropdownToggle : null} focusable={variant === 'DROPDOWN'} pointerEvents={variant === 'DROPDOWN' ? 'box-only' : 'box-none'} disabled={disabled} style={{ width: '100%' }}>
        {/* Inputfield border color will be animated later */}
        <Animated.View style={[
            styles.container,
            {
                backgroundColor: disabled ? BMComponents.BMUiInputBgcolorDisabled : BMComponents.BMUiInputBgcolor,
                borderColor: getBorderColor(),
                height: variant === 'AREA' ? INPUT_HEIGHT * 2 : INPUT_HEIGHT,
                alignItems: variant === 'AREA' ? 'flex-start' : 'center',
            },
        ]} onLayout={measureSize}>
          {LeadingIcon && (<View style={[styles.leadingIconContainer, { paddingTop: variant === 'AREA' ? 4 : 0 }]}>
              <LeadingIcon width={16} height={16}/>
            </View>)}
          <TextInput style={{
            ...BMPrimitives.BMTypography.paragraphNormal,
            color: disabled ? BMComponents.BMStyleTextInkMuted : BMComponents.BMStyleTextInkPrimary,
            // @ts-ignore
            outlineStyle: 'none',
            height: variant === 'AREA' ? INPUT_HEIGHT * 2 - 20 : '100%',
            flex: 1,
            padding: 0,
            margin: 0,
            ...Platform.select({
                ios: {
                    lineHeight: 0,
                },
            }),
            textAlignVertical: 'top',
            width: '100%',
        }} {...restProps} testID={testID} ref={setTextRef} multiline={variant === 'AREA'} editable={!disabled && editable} value={value} onChangeText={handleTextChange} placeholderTextColor={BMComponents.BMStyleTextInkMuted} onFocus={handleFocus} onBlur={handleBlur}/>
          {buttonInsetProps && (<View style={styles.trailingButtonContainer}>
              <ButtonInsetComponent {...buttonInsetProps} size="INSET" role="SECONDARY" disabled={disabled || buttonInsetProps.disabled} timerDuration={0}/>
            </View>)}

          {/* Focus ring border  */}
          {/* will add animation in/out later */}
          <Animated.View style={{
            borderColor: BMComponents.BMStyleLineBordercolorFocus,
            borderWidth: 3,
            position: 'absolute',
            ...containerSize,
            borderRadius: 10,
            left: -2,
            opacity: isFocused && !disabled ? 1 : 0,
            top: -2,
        }} pointerEvents="none"/>
        </Animated.View>
      </Pressable>
      {showTextMessage && (<BMText style={[styles.caption, { textAlign: captionAlignment }]} variant="uiSmall" ink={error ? 'BMUiInputInkError' : 'BMStyleTextInkSecondary'}>
          {error || caption}
        </BMText>)}
    </>);
});
export default BMInputField;
const styles = StyleSheet.create({
    container: {
        flexDirection: 'row',
        borderRadius: 10,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderWidth: BMPrimitives.BMBorder.width.thick,
    },
    leadingIconContainer: {
        marginRight: BMPrimitives.BMSize.xs,
    },
    trailingButtonContainer: {
        marginLeft: BMPrimitives.BMSize.xs,
    },
    label: {
        marginLeft: BMPrimitives.BMSize.xs,
        marginBottom: BMPrimitives.BMSize.xs,
    },
    caption: {
        marginLeft: BMPrimitives.BMSize.xs,
        marginTop: BMPrimitives.BMSize.xs,
    },
});
